/**
 * ProductsView - NVIDIA Products page view
 *
 * Displays NVIDIA products and related information with pagination
 */

import { BaseView } from './BaseView';
import { DataService } from '../services/DataService';
import type { Product } from '@/types';
import * as XLSX from 'xlsx';

export class ProductsView extends BaseView {
	private dataService: DataService;
	private allProducts: Product[] = [];
	private currentPage: number = 0;
	private readonly pageSize: number = 25;

	constructor() {
		super();
		this.dataService = new DataService();
	}

	/**
	 * Render the products view content
	 */
	render(): string {
		return `
			<style>
				.products-table {
					width: 100%;
					border-collapse: collapse;
					font-family: Arial, sans-serif;
					font-size: 11px;
					background: #fff;
					margin-top: 10px;
				}

				.products-table th {
					background: #e6e8ea;
					border: 1px solid #999;
					padding: 6px 8px;
					font-weight: bold;
					color: #333;
					text-align: left;
					position: relative;
				}

				.products-table th.sortable {
					cursor: pointer;
				}

				.products-table th.sortable:hover {
					background: #d6d8da;
				}

				.products-table th .sort-arrow {
					float: right;
					font-size: 10px;
					color: #666;
				}

				.products-table td {
					border: 1px solid #ccc;
					padding: 4px 8px;
					color: #333;
					vertical-align: top;
				}

				.products-table tr:nth-child(even) {
					background: #f8f9fa;
				}

				.products-table tr:hover {
					background: #e8f4fd;
				}

				.product-name {
					color: #111;
					text-decoration: none;
					font-family: 'Ek Mukta', Arial, sans-serif;
					font-size: 14px;
					font-weight: 400;
				}

				.company-icon {
					width: 16px;
					height: 16px;
					margin-right: 4px;
					vertical-align: middle;
				}

				.source-text {
					color: #333;
					font-size: 11px;
				}

				.company-cell .company-name {
					color: #333;
					font-size: 11px;
				}

				.pagination {
					margin-top: 15px;
					font-size: 11px;
					color: #333;
				}

				.pagination a {
					color: #0056d6;
					text-decoration: none;
					margin: 0 2px;
				}

				.pagination a:hover {
					text-decoration: underline;
				}

				.pagination .current {
					font-weight: bold;
					color: #333;
				}

				.footnote-products {
					margin-top: 10px;
					font-size: 10px;
					color: #666;
				}

				.products-table td.company-cell {
					font-size: 10px;
				}

				.products-table td.product-name-cell {
					background: #e9f0f7;
					color: #111;
				}

				@media print {
					.header,
					.navbar,
					.sidebar,
					.button-row {
						display: none !important;
					}

					@page {
						size: letter;
						margin: 0.5in;
					}

					body {
						margin: 0;
						padding: 0;
					}

					.content-container {
						display: block;
					}

					.main-content {
						width: 100% !important;
						margin: 0 !important;
					}
				}
			</style>

			<div class="company-header">
				<div class="company-name">NVIDIA Corporation (NasdaqGS:NVDA) Products</div>
			</div>

			<div class="button-row">
				<div class="tool-button" id="export-excel-btn">
					<img src="../img/icon1.png" alt="Export" onerror="this.src='../img/icon-placeholder.png'">
					<span class="button-text">Export to Excel</span>
				</div>
				<div class="tool-button print-button">
					<i data-lucide="printer" style="width: 16px; height: 16px; margin-right: 3px;"></i>
					<span class="button-text">Print</span>
				</div>
			</div>

			<div class="main-section">
				<div style="width:98%; margin:auto; margin-top:18px;">
					<table class="products-table" style="overflow:hidden;">
						<thead>
							<tr>
								<th colspan="4" style="background: #e6e8ea; font-size: 15px; font-weight: bold; color: #333; text-align: left; padding: 8px 10px; border-bottom: 2px solid #bbb;">Products</th>
							</tr>
							<tr>
								<th style="width: 20px;"><i data-lucide="clipboard" style="width: 16px; height: 16px;"></i></th>
								<th class="sortable" style="width: 35%;">Product Name <span class="sort-arrow">▲</span></th>
								<th style="width: 35%;">Company</th>
								<th style="width: 30%;">Source</th>
							</tr>
						</thead>
						<tbody id="products-tbody"></tbody>
					</table>
				</div>

				<div class="pagination" id="products-pagination"></div>

				<div class="footnote-products">
					*denotes proprietary relationship
				</div>
			</div>
		`;
	}

	/**
	 * Initialize products table after view is mounted
	 */
	async onMount(): Promise<void> {
		try {
			// Load products from DataService
			this.allProducts = await this.dataService.loadProducts();

			// Set up Export to Excel button handler
			const exportBtn = this.container?.querySelector('#export-excel-btn');
			if (exportBtn) {
				this.addEventListener(exportBtn as HTMLElement, 'click', () => {
					this.exportToExcel();
				});
			}

			// Set up Print button handler
			const printBtn = this.container?.querySelector('.print-button');
			if (printBtn) {
				this.addEventListener(printBtn as HTMLElement, 'click', () => {
					window.print();
				});
			}

			// Render initial page
			this.renderProductsTable();
			this.updatePagination();
		} catch (error) {
			console.error('[ProductsView] Failed to load products:', error);
		}
	}

	/**
	 * Render products table for current page
	 */
	private renderProductsTable(): void {
		const tbody = this.container?.querySelector('#products-tbody');

		if (!tbody) {
			console.warn('[ProductsView] Products tbody not found');
			return;
		}

		tbody.innerHTML = '';

		// Calculate page bounds
		const startIdx = this.currentPage * this.pageSize;
		const endIdx = Math.min(startIdx + this.pageSize, this.allProducts.length);
		const pageData = this.allProducts.slice(startIdx, endIdx);


		pageData.forEach(prod => {
			const tr = document.createElement('tr');

			const companyIconHtml = prod.companyIcon
				? `<img src="${prod.companyIcon}" alt="" class="company-icon" onerror="this.style.display='none'">`
				: '';

			tr.innerHTML = `
				<td><i data-lucide="clipboard" style="width: 16px; height: 16px;"></i></td>
				<td class="product-name-cell"><span class="product-name">${prod.name}</span></td>
				<td class="company-cell">${companyIconHtml}<span class="company-name">${prod.company}</span></td>
				<td><span class="source-text">${prod.source}</span></td>
			`;

			tbody.appendChild(tr);
		});

		// Initialize Lucide icons for dynamically added content
		if (typeof window.lucide !== 'undefined') {
			window.lucide.createIcons();
		}
	}

	/**
	 * Update pagination links
	 */
	private updatePagination(): void {
		const pagDiv = this.container?.querySelector('#products-pagination');

		if (!pagDiv) {
			return;
		}

		const totalProducts = this.allProducts.length;
		const totalPages = Math.ceil(totalProducts / this.pageSize);
		const startIdx = this.currentPage * this.pageSize + 1;
		const endIdx = Math.min((this.currentPage + 1) * this.pageSize, totalProducts);

		// Build pagination links
		const links: string[] = [];

		for (let i = 0; i < Math.min(totalPages, 6); i++) {
			const pageStart = i * this.pageSize + 1;
			const pageEnd = Math.min((i + 1) * this.pageSize, totalProducts);

			if (i === this.currentPage) {
				links.push(`<span class="current">View ${pageStart}-${pageEnd}</span>`);
			} else {
				links.push(`<a href="#" data-page="${i}">${pageStart}-${pageEnd}</a>`);
			}
		}

		const nextPageLink = this.currentPage < totalPages - 1
			? `<a href="#" data-action="next">&gt;</a>`
			: `<span>&gt;</span>`;
		const lastPageLink = this.currentPage < totalPages - 1
			? `<a href="#" data-action="last">&gt;&gt;</a>`
			: `<span>&gt;&gt;</span>`;

		pagDiv.innerHTML = `Viewing ${startIdx}-${endIdx} of ${totalProducts} Products [${links.join(' | ')} | ${nextPageLink} ${lastPageLink}] [<a href="#" data-action="viewAll">View All</a>]`;

		// Attach click handlers for page links
		const pageLinks = pagDiv.querySelectorAll('a[data-page]');
		pageLinks.forEach(link => {
			this.addEventListener(link as HTMLElement, 'click', (e: Event) => {
				e.preventDefault();
				const target = e.currentTarget as HTMLElement;
				const pageIdx = parseInt(target.getAttribute('data-page') || '0', 10);
				this.currentPage = pageIdx;
				this.renderProductsTable();
				this.updatePagination();
			});
		});

		// Attach click handler for Next Page
		const nextLink = pagDiv.querySelector('a[data-action="next"]');
		if (nextLink) {
			this.addEventListener(nextLink as HTMLElement, 'click', (e: Event) => {
				e.preventDefault();
				this.goToNextPage();
			});
		}

		// Attach click handler for Last Page
		const lastLink = pagDiv.querySelector('a[data-action="last"]');
		if (lastLink) {
			this.addEventListener(lastLink as HTMLElement, 'click', (e: Event) => {
				e.preventDefault();
				this.goToLastPage();
			});
		}

		// Attach click handler for View All
		const viewAllLink = pagDiv.querySelector('a[data-action="viewAll"]');
		if (viewAllLink) {
			this.addEventListener(viewAllLink as HTMLElement, 'click', (e: Event) => {
				e.preventDefault();
				this.viewAllProducts();
			});
		}
	}

	/**
	 * Navigate to next page
	 */
	private goToNextPage(): void {
		const totalPages = Math.ceil(this.allProducts.length / this.pageSize);
		if (this.currentPage < totalPages - 1) {
			this.currentPage++;
			this.renderProductsTable();
			this.updatePagination();
		}
	}

	/**
	 * Navigate to last page
	 */
	private goToLastPage(): void {
		const totalPages = Math.ceil(this.allProducts.length / this.pageSize);
		this.currentPage = totalPages - 1;
		this.renderProductsTable();
		this.updatePagination();
	}

	/**
	 * View all products (disable pagination)
	 */
	private viewAllProducts(): void {
		const pagDiv = this.container?.querySelector('#products-pagination');
		const tbody = this.container?.querySelector('#products-tbody');

		if (!tbody || !pagDiv) {
			console.warn('[ProductsView] Products tbody or pagination not found');
			return;
		}

		tbody.innerHTML = '';


		// Render all products
		this.allProducts.forEach(prod => {
			const tr = document.createElement('tr');

			const companyIconHtml = prod.companyIcon
				? `<img src="${prod.companyIcon}" alt="" class="company-icon" onerror="this.style.display='none'">`
				: '';

			tr.innerHTML = `
				<td><i data-lucide="clipboard" style="width: 16px; height: 16px;"></i></td>
				<td class="product-name-cell"><span class="product-name">${prod.name}</span></td>
				<td class="company-cell">${companyIconHtml}<span class="company-name">${prod.company}</span></td>
				<td><span class="source-text">${prod.source}</span></td>
			`;

			tbody.appendChild(tr);
		});

		// Initialize Lucide icons for dynamically added content
		if (typeof window.lucide !== 'undefined') {
			window.lucide.createIcons();
		}

		// Update pagination to show "Viewing All"
		pagDiv.innerHTML = `Viewing all ${this.allProducts.length} Products`;
	}

	/**
	 * Export products data to Excel
	 */
	private exportToExcel(): void {
		try {
			// Prepare data for Excel export
			const excelData = this.allProducts.map(prod => ({
				"Product Name": prod.name,
				"Company": prod.company,
				"Source": prod.source
			}));

			// Create worksheet
			const worksheet = XLSX.utils.json_to_sheet(excelData);

			// Create workbook
			const workbook = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');

			// Generate filename with current date
			const dateStr = new Date().toISOString().split('T')[0];
			const filename = `NVIDIA-Products-${dateStr}.xlsx`;

			// Download file
			XLSX.writeFile(workbook, filename);

		} catch (error) {
			console.error('[ProductsView] Failed to export to Excel:', error);
		}
	}
}
